import { useLocation, useNavigate } from 'react-router-dom'
import { KTSVG } from '../../../../_metronic/helpers'
import { TableHeader } from './TableHeader'
import { DynamicDataAGTable } from './DynamicDataAGTable'
import {
  DynamicTableDataInputType,
  DynamicTableDataType,
  DynamicTableDataInput,
} from '../wrappers/DynamicTablesPageWrapper'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { MenuComponent } from '../../../../_metronic/assets/ts/components'
import {
  DynamicTableFilterInput,
  DynamicTablePublicAccessInput,
  GetAllDynamicTableDataDocument,
  GetDynamicFilterInput,
  SyncGoogleSheetToS3Mutation,
  SyncGoogleSheetToS3MutationVariables,
  useGetAllDynamicTableDataQuery,
  useSyncGoogleSheetToS3Mutation,
} from '../../../../generated/graphql'
import { set } from 'date-fns'
import { useCanUserAccess } from '../../store/userRoleGrants'
import { Link } from 'lucide-react'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import Toast, { ToastType } from '../../../../utils/toast'
import { fetchData } from './MainDynamicAGComponent'

type DynamicTableDataProps = {
  data?: DynamicTableDataInputType
  isSuperAdmin?: boolean
  existingData?: DynamicTableDataType
  isExisting: boolean
  handleBack: Function
  handleSubmit: Function
  filterData?: any
  saveFilter: Function
  removeFilter: Function
  publishTable?: Function
  isPublishedTable?: boolean
  loaders?: any
  onSyncData?: Function
}

export enum DynamicAction {
  UPDATE = 'Update',
  DELETE = 'Delete',
  ADD = 'Add',
}

export function IndividualTablePage(props: DynamicTableDataProps) {
  const location = useLocation()

  // Memoize stateData to prevent infinite re-renders
  const stateData = useMemo(() => {
    const state = location.state || [props.data]
    return Array.isArray(state) ? state : [state]
  }, [location.state, props.data])

  const [discard, setDiscard] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [sheetLink, setSheetLink] = useState<string>(
    props.isExisting ? (props.existingData && props.existingData?.googleSheetLink) || '' : ''
  )
  const [chartData, setChartData] = useState<any[]>(
    props.isExisting ? props.existingData && props.existingData.chartData : undefined
  )

  // Memoize computed values to prevent infinite re-renders
  const data = useMemo(() => {
    return stateData?.length > 0 ? stateData?.length > 0 && stateData[0]?.data : []
  }, [stateData])

  const tableData = useMemo(() => {
    return { data: data, indexData: stateData?.length > 0 && stateData[0] }
  }, [data, stateData])

  const finalData = useMemo(() => {
    return props.isExisting ? props.existingData?.tableData.data : data
  }, [props.isExisting, props.existingData?.tableData.data, data])

  const finalTableData = useMemo(() => {
    return props.isExisting
      ? props.existingData?.tableData.indexData
      : stateData?.length > 0 && stateData[0]
  }, [props.existingData?.tableData.indexData, stateData])

  const name = useMemo(() => {
    return props.isExisting
      ? props.existingData?.name
      : (stateData?.length > 0 &&
          stateData[0].indexData?.length > 0 &&
          stateData[0].indexData[0]?.title) ||
          ''
  }, [props.isExisting, props.existingData?.name, stateData])

  const description = useMemo(() => {
    return props.isExisting
      ? props.existingData?.description
      : (stateData?.length > 0 &&
          stateData[0].indexData?.length > 0 &&
          stateData[0].indexData[0].description) ||
          ''
  }, [props.isExisting, props.existingData?.description, stateData])

  const sheetNames = useMemo(() => {
    return props.isExisting
      ? props.existingData?.tableData?.indexData?.indexData
          ?.map((indexData: any, index: number) => indexData.tableTitle)
          .flat()
      : stateData[0]?.indexData?.map((indexData: any, index: number) => indexData.tableTitle).flat()
  }, [props.isExisting, props.existingData?.tableData?.indexData?.indexData, stateData])

  const [selectedTables, setSelectedTables] = useState<any[]>([])
  const isDynamicTableRole = useCanUserAccess('read', 'dynamicTable')

  // Initialize selectedTables when sheetNames changes
  useEffect(() => {
    if (sheetNames && sheetNames.length > 0) {
      setSelectedTables((prevTables) => {
        // Only update if the length or names have actually changed
        if (
          prevTables.length !== sheetNames.length ||
          prevTables.some((table, index) => table.name !== sheetNames[index])
        ) {
          return sheetNames.map((name: string) => ({
            name: name,
            isChecked: true,
          }))
        }
        return prevTables
      })
    }
  }, [sheetNames])
  const [filteredData, setFilteredData] = useState<any>([])
  const [filterTableData, setFilterTableData] = useState<any>(null)

  const [isChartDataLoaded, setIsChartDataLoaded] = useState(false)

  // Initialize filtered data when finalData changes
  useEffect(() => {
    if (finalData) {
      setFilteredData(finalData)
    }
  }, [finalData])

  // Initialize filter table data when finalTableData changes
  useEffect(() => {
    if (finalTableData) {
      setFilterTableData(finalTableData)
    }
  }, [finalTableData])

  // Reset chartData when finalTableData changes
  useEffect(() => {
    setIsChartDataLoaded(false)
    if (props.isExisting && props.existingData?.chartData) {
      setChartData(props.existingData.chartData)
    } else {
      setChartData([])
    }
    // Mark chartData as loaded after a small delay to ensure state is updated
    setTimeout(() => {
      setIsChartDataLoaded(true)
    }, 100)
  }, [props.isExisting, props.existingData?.chartData])

  // Initialize container when chartData is loaded
  useEffect(() => {
    if (isChartDataLoaded) {
      const container = document.getElementById('container')
      if (container) {
        // Clear any existing charts in the container
        container.innerHTML = ''
        container.style.display = 'flex'
      }
    }
  }, [isChartDataLoaded])

  useEffect(() => {
    if (selectedTables.length > 0 && finalData && finalTableData) {
      const filteredResults = finalData.filter((data: any, index: number) => {
        return selectedTables[index]?.isChecked === true
      })

      const filteredTableResults = finalTableData?.indexData?.filter((data: any, index: number) => {
        return selectedTables[index]?.isChecked === true
      })
      const filteredTablesData = { ...finalTableData, indexData: filteredTableResults }
      setFilteredData(filteredResults)
      setFilterTableData(filteredTablesData)
    }
  }, [selectedTables, finalData, finalTableData])

  const saveData = useCallback(() => {
    const finalDataToSave: DynamicTableDataInput = {
      name: name,
      description: description,
      tableData: [],
      chartData,
      file: props.isExisting ? undefined : props.data?.file!,
      isExisting: props.isExisting,
      isPublished: props.isExisting ? props.existingData?.isPublished ?? false : false,
      id: props.isExisting ? props.existingData?.id : undefined,
    }
    props.handleSubmit(finalDataToSave)
  }, [
    name,
    description,
    chartData,
    props.isExisting,
    props.data?.file,
    props.existingData?.isPublished,
    props.existingData?.id,
    props.handleSubmit,
  ])

  const PublishData = useCallback(() => {
    const publishData: DynamicTablePublicAccessInput = {
      id: props.isExisting ? Number(props.existingData?.id) : undefined,
      isPublic: true,
    }
    if (props.publishTable) props.publishTable(publishData)
  }, [props.isExisting, props.existingData?.id, props.publishTable])

  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization()
    }, 50)
  }, [])

  const mutateSyncDataSheet = useSyncGoogleSheetToS3Mutation<Error>(graphqlRequestClient, {
    onSuccess: async (
      data: SyncGoogleSheetToS3Mutation,
      variables: SyncGoogleSheetToS3MutationVariables,
      _context: unknown
    ) => {
      queryClient.invalidateQueries(useGetAllDynamicTableDataQuery.getKey())

      const freshData = await queryClient.fetchQuery({
        queryKey: useGetAllDynamicTableDataQuery.getKey(),
        queryFn: () => graphqlRequestClient.request(GetAllDynamicTableDataDocument),
      })
      if (freshData) {
        const tables = freshData?.getDynamicTableData ?? []

        const currentTable = await tables.find(
          (table: DynamicTableDataType) => String(table.id) === String(props.existingData?.id)
        )

        if (currentTable && currentTable?.fileUrl) {
          const updatedData = await fetchData(currentTable?.fileUrl)
          if (updatedData) {
            const newData = updatedData
              ? {
                  ...props.existingData,
                  tableData: {
                    data: updatedData[0].data,
                    indexData: updatedData[0],
                  },
                }
              : props.existingData
            props.onSyncData && props.onSyncData(newData)
          }
        }
      }
      Toast('Your File Synced Successfully', ToastType.success)
      setLoading(false)
      setShowSyncModal(false)
    },
    onError: (error: any) => {
      Toast(error.response.errors[0].message, ToastType.error)
      setLoading(false)
    },
  })

  const buttonOptions = ['Publish', 'Sync Google Sheets']
  const icons = [
    <KTSVG
      path='/media/icons/duotune/files/fil023.svg'
      className='svg-icon-2x text-gray-500 text-hover-primary'
    />,
    <KTSVG
      path='/media/icons/duotune/files/fil011.svg'
      className='svg-icon-2x text-gray-500 text-hover-primary'
    />,
  ]

  const actionMenu = () => {
    let newOptions = buttonOptions
    return newOptions.map((option, index) => {
      return (
        <label
          key={option}
          className='form-check-custom py-3 bg-hover-secondary px-2 text-hover-primary'
          onClick={() => {
            if (option === 'Publish') {
              PublishData()
            } else if (option === 'Sync Google Sheets') {
              setShowSyncModal(true)
            }
          }}
        >
          <span className='form-check-label fs-base fw-normal cursor-pointer fw-bold ms-1'>
            {icons[index]} {option}
          </span>
        </label>
      )
    })
  }

  const HandleSyncSheet = () => {
    setLoading(true)
    mutateSyncDataSheet.mutate({
      input: { sheetUrl: sheetLink, id: props.existingData?.id || 0 },
    })
  }

  return (
    <>
      <div className='col d-flex flex-row justify-content-between pb-4'>
        {!props.isPublishedTable && (
          <button
            type='button'
            onClick={() => {
              isUpdated === false ? props.handleBack() : setDiscard(true)
            }}
            className='px-4 rounded border border-1 border-hover-primary border-secondary-txt bg-transparent h-38px fw-normal text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/arrows/arr021.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Back
          </button>
        )}

        <div className='d-flex flex-row g-4'>
          {!props.isPublishedTable && (
            <div>
              <button
                type='button'
                onClick={() => {
                  saveData()
                  setIsUpdated(false)
                }}
                className='px-4 fs-7 rounded border-0 bg-primary h-38px fw-normal ms-4 text-white d-inline-flex align-items-center gap-2'
              >
                <KTSVG
                  path='/media/icons/duotune/files/fil017.svg'
                  className='svg-icon-1 text-white'
                />
                {props.loaders?.isSaveLoader ? (
                  <span className='indicator-progress d-inline-flex align-items-center gap-2'>
                    Saving...
                    <span className='spinner-border spinner-border-sm align-middle'></span>
                  </span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          )}
          {/* {props.isSuperAdmin && props.existingData && !props.isPublishedTable && (
            <div>
              <button
                type='button'
                onClick={PublishData}
                className='px-4 fs-7 rounded border-0 bg-primary h-38px fw-normal ms-4 text-white d-inline-flex align-items-center gap-2'
              >
                <KTSVG
                  path='/media/icons/duotune/files/fil023.svg'
                  className='svg-icon-2x text-white'
                />
                {props.loaders?.isPublishLoader ? (
                  <span className='indicator-progress d-inline-flex align-items-center gap-2'>
                    Publishing...
                    <span className='spinner-border spinner-border-sm align-middle'></span>
                  </span>
                ) : (
                  'Publish'
                )}
              </button>
            </div>
          )} */}
          {props.isSuperAdmin && props.existingData && !props.isPublishedTable && (
            <div className='px-0'>
              <button
                type='button'
                className='px-3 rounded border border-0 border-primary bg-primary h-38px fw-normal ms-4 text-white dropdown-toggle'
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                data-kt-menu-flip='top-end'
                data-bs-toggle='dropdown'
              >
                Actions{' '}
              </button>
              <div
                className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-bold w-175px py-2 mt-1 border border-1 border-gray-400'
                data-kt-menu='true'
                style={{
                  boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
                  border: '1',
                  borderColor: '#F1F1F2',
                }}
              >
                {actionMenu()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className='row mb-9'>
        <div className='col-12'>
          <TableHeader reportName={name} description={description} id='1' />
        </div>
      </div>

      <div id='container' className='row d-flex overflow-auto'></div>

      {/* AG Table - Only render when chartData is loaded */}
      {isChartDataLoaded &&
        filteredData &&
        filteredData?.map((data: any, index: number) => {
          return (
            <div className='row mb-9'>
              <div className='col-12'>
                <DynamicDataAGTable
                  tableId={props.existingData?.id! || 0}
                  data={data}
                  savedCharts={
                    props.isExisting
                      ? props.existingData?.chartData?.filter(
                          (chart: any) => chart?.chartIndex === index
                        )
                      : []
                  }
                  name={
                    props.isExisting
                      ? filterTableData?.indexData[index]?.tableTitle[0] ||
                        filterTableData?.indexData[index]?.tableTitle
                      : stateData[0]?.indexData[index]?.tableTitle || name
                  }
                  tableData={filterTableData}
                  handleChartData={(values: any) => {
                    setChartData(values)
                  }}
                  handleUpdate={(values: boolean) => {
                    setIsUpdated(values)
                  }}
                  index={index}
                  chartData={chartData || []}
                  setChartData={(values: any[], action: DynamicAction, tableIndex: number) => {
                    setChartData((prev: any[]) => {
                      if (action === DynamicAction.ADD) {
                        const existingIds = new Set(prev?.map((item) => item.myChartId))
                        const newValues = values.filter(
                          (value) => !existingIds.has(value.myChartId)
                        )
                        return prev ? [...prev, ...newValues] : [...newValues]
                      } else {
                        const remainingTableData = prev?.filter(
                          (item) => item.chartIndex !== tableIndex
                        )
                        const newValues = [...remainingTableData, ...values]
                        return newValues
                      }
                    })
                  }}
                  filterData={props.filterData}
                  saveFilter={(values: DynamicTableFilterInput) => props.saveFilter(values)}
                  removeFilter={(values: GetDynamicFilterInput) => props.removeFilter(values)}
                />
              </div>
            </div>
          )
        })}
      {discard && isUpdated && (
        <Modal
          id='kt_modal_add_inv'
          tabIndex={-1}
          aria-hidden='true'
          dialogClassName='modal-dialog modal-dialog-centered mw-500px '
          show={discard}
          onHide={() => setDiscard(false)}
        >
          <Modal.Body className='p-9'>
            <div className='card'>
              <div className='card-body p-0'>
                <div className='fs-5 pb-2 fw-bold'>
                  Are you sure you want to navigate back to the main table page?
                </div>
                <div className='fs-7 fw-normal pb-2'>
                  <b>Note : </b> Clicking back will remove this data. Please save your work before
                  proceeding.
                </div>

                <div className='d-flex col justify-content-between pt-6'>
                  <button
                    className='px-4 rounded border border-1 border-primary bg-transparent h-38px fw-normal text-primary'
                    onClick={() => setDiscard(false)}
                  >
                    <span className='indicator-label'>Cancel</span>
                  </button>
                  <button
                    type='submit'
                    className='px-4 rounded border border-0 border-primary bg-primary h-38px fw-normal text-white'
                    onClick={() => props.handleBack()}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
      {showSyncModal && (
        <Modal
          id='kt_modal_add_inv'
          tabIndex={-1}
          aria-hidden='true'
          dialogClassName='modal-dialog modal-dialog-centered mw-500px '
          show={showSyncModal}
          onHide={() => setShowSyncModal(false)}
        >
          <Modal.Body className='p-9'>
            <div className='card'>
              <div className='card-body p-0'>
                <h2 className='fw-bolder text-gray-heading pb-4 d-flex flex-row align-items-center'>
                  <div className='col-1 mt-2'>
                    <Link
                      className='ms-2 text-success bg-success-badge p-3 rounded-circle'
                      size={40}
                    />
                  </div>
                  <span className='col-10 ms-5'>
                    {'Google Sheet Link'}
                    <div className='fs-8 text-gray-500'>
                      {'Enter the updated google sheet link to sync the data'}
                    </div>
                  </span>
                </h2>

                <div className='fs-8 fw-normal pb-4'>
                  <b>Note : </b>{' '}
                  {`Make sure the Google Sheet is accessible and correctly formatted, as any changes in the sheet's data will directly impact the saved graphs and filters.`}
                </div>
                <div className='fs-8 fw-normal pb-4 gap-2'>
                  <b>Note : </b> To grant access to the Google Sheet, please share it with the
                  service account email {''}{' '}
                  <b className='fs-7'>"gdrive@hissa-443008.iam.gserviceaccount.com"</b>
                </div>
                <div className='fv-row mb-6 col-12'>
                  <label className='form-label required'>Google Sheet Link</label>

                  {/* <InputBox
                    isDisabled={false}
                    name={'sheetLink'}
                    handleChange={(value: string) => {
                      setSheetLink(value)
                    }}
                    setFieldValue={(value: string) => {
                      setSheetLink(value)
                    }}
                    placeholder='Enter the Google Sheet Link'
                    currency={'INR - ₹'}
                    value={sheetLink}
                    type='text'
                  /> */}
                  <textarea
                    name='notes'
                    className='form-control me-2 custom-input fw-bold shadow-lg '
                    placeholder='Enter the Google Sheet Link'
                    onChange={(e) => {
                      setSheetLink(e.target.value)
                    }}
                    value={sheetLink}
                    rows={3}
                  />
                </div>
                <div className='d-flex col justify-content-between pt-6'>
                  <button
                    className='px-4 rounded border border-1 border-primary bg-transparent h-38px fw-normal text-primary'
                    onClick={() => setShowSyncModal(false)}
                  >
                    <span className='indicator-label'>Cancel</span>
                  </button>
                  <button
                    type='submit'
                    className={`px-4 rounded border border-0 border-primary  h-38px fw-normal text-white ${
                      sheetLink.trim() === '' ? 'disabled bg-primary-light' : 'bg-primary'
                    }`}
                    onClick={() => HandleSyncSheet()}
                    disabled={sheetLink.trim() === ''}
                  >
                    {loading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Syncing...
                      </>
                    ) : (
                      'Sync'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
type filterTypes = {
  isAllSelected?: boolean
  selectedTables: any[]
  setSelectedTables: Function
  setIsAllSelected?: Function
}
